import { useEffect, useState } from 'react';
import {
    ArrowUpOnSquareIcon,
    ComputerDesktopIcon,
    PlusCircleIcon,
} from '@heroicons/react/24/outline';

import Button from '../../components/Button';
import Text from '../../components/Text';
import type { ClePlateforme } from '../../helpers/plateforme';
import { t } from '../../i18n/langue';

import {
    Avertissement,
    CopierAdresse,
    EnteteSas,
    Etapes,
    HOTE_MOBILE,
    HOTE_WEB,
    PiedSas,
    TexteRiche,
} from './briques';

/**
 * Les six consignes d'installation — #21, portées du prototype
 * `prototype/mobile-install`.
 *
 * Elles ne s'opposent pas, elles se suivent : la chaîne est tranchée, ce fichier
 * ne fait que l'écrire proprement. Le chrome de navigateur dessiné dans le
 * prototype n'est pas repris — il portait la moitié de la consigne en maquette,
 * mais sur un vrai téléphone le bouton Partager est réellement là, sous les yeux.
 */

interface ConsigneProps {
    onChoisirConsigne: (cle: ClePlateforme) => void;
}

// ── 1. iPhone · Safari — le cas nominal ─────────────────────────────────────

export function ConsigneIosSafari({ onChoisirConsigne }: ConsigneProps) {
    return (
        <>
            <EnteteSas accroche={t('sas.iosSafari.accroche')} />

            <Etapes
                items={[
                    <TexteRiche
                        key="partager"
                        texte={t('sas.iosSafari.etape1')}
                        icone={
                            <ArrowUpOnSquareIcon
                                aria-hidden="true"
                                className="inline h-4 w-4 -translate-y-0.5 text-main-color"
                            />
                        }
                    />,
                    <TexteRiche key="ecran" texte={t('sas.iosSafari.etape2')} />,
                    <TexteRiche key="ajouter" texte={t('sas.iosSafari.etape3')} />,
                ]}
            />

            <Avertissement>
                {t('sas.iosSafari.avertissement')}
            </Avertissement>

            <PiedSas onChoisirConsigne={onChoisirConsigne} />
        </>
    );
}

// ── 2. iPhone · navigateur tiers ────────────────────────────────────────────

/**
 * Le détour par Safari, et pourquoi il reste.
 *
 * #35 a établi que le manifeste — et non le navigateur qui installe — décide
 * qu'une icône est une vraie web app : « This is true no matter which browser
 * added the website to the Home Screen » (WebKit). Chrome iOS fait ce qu'il faut,
 * vérifié dans son source ; Firefox est douteux depuis un refactor d'avril 2025 ;
 * Edge est inconnu.
 *
 * #39 a donc tranché **par défaut, dans le sens sûr**. La consigne est
 * *raccourcissable* le jour où quelqu'un pose la main sur un iPhone équipé de ces
 * navigateurs — pas avant. Et ce qui rend l'assouplissement futur sans risque est
 * le mur du sas lui-même : un simple raccourci ouvre le navigateur, donc hors
 * standalone, donc le sas se réaffiche et redonne la consigne. Le mauvais résultat
 * coûte une boucle, pas un utilisateur.
 */
export function ConsigneIosTiers({ onChoisirConsigne }: ConsigneProps) {
    return (
        <>
            <EnteteSas accroche={t('sas.iosTiers.accroche')} />

            <Etapes
                items={[
                    <TexteRiche key="menu" texte={t('sas.iosTiers.etape1')} />,
                    <TexteRiche key="safari" texte={t('sas.iosTiers.etape2')} />,
                    <TexteRiche key="reprendre" texte={t('sas.iosTiers.etape3')} />,
                ]}
            />

            <div className="px-6">
                <CopierAdresse />
                <Text variante="petit" ton="attenue" className="mt-2 block text-center">
                    <TexteRiche texte={t('sas.iosTiers.sansMenu', { hote: HOTE_MOBILE })} />
                </Text>
            </div>

            <PiedSas onChoisirConsigne={onChoisirConsigne} />
        </>
    );
}

// ── 3. Navigateur in-app — là où atterrissent les invitations ───────────────

export function ConsigneInApp({ onChoisirConsigne }: ConsigneProps) {
    return (
        <>
            <EnteteSas accroche={t('sas.inApp.accroche')} />

            <Etapes
                items={[
                    <TexteRiche key="menu" texte={t('sas.inApp.etape1')} />,
                    <TexteRiche key="navigateur" texte={t('sas.inApp.etape2')} />,
                    <TexteRiche key="consigne" texte={t('sas.inApp.etape3')} />,
                ]}
            />

            <div className="px-6">
                <CopierAdresse />
                <Text variante="petit" ton="attenue" className="mt-2 block text-center">
                    <TexteRiche texte={t('sas.inApp.sinon', { hote: HOTE_MOBILE })} />
                </Text>
            </div>

            <PiedSas onChoisirConsigne={onChoisirConsigne} />
        </>
    );
}

// ── 4. Android · Chrome — le seul cas programmable ──────────────────────────

/**
 * `beforeinstallprompt` n'existe que sur Chromium, jamais sur WebKit.
 *
 * L'événement est capté au montage et rejoué au tap. Deux précautions qui ne sont
 * pas décoratives : il ne se déclenche **pas toujours** (critères d'engagement de
 * Chrome, app déjà installée, manifeste rejeté), et il n'est **utilisable
 * qu'une fois**. Le guide manuel reste donc affiché en dessous, en permanence —
 * un bouton qui disparaît sans rien faire est pire que pas de bouton.
 */
export function ConsigneAndroidChrome({ onChoisirConsigne }: ConsigneProps) {
    const [invite, setInvite] = useState<Event | null>(null);

    useEffect(() => {
        const capter = (e: Event) => {
            // Empêche la bannière native de Chrome : on veut que l'installation
            // parte de NOTRE bouton, sous la phrase qui explique pourquoi.
            e.preventDefault();
            setInvite(e);
        };

        window.addEventListener('beforeinstallprompt', capter);
        return () => window.removeEventListener('beforeinstallprompt', capter);
    }, []);

    const installer = async () => {
        if (!invite) return;
        // L'événement porte `prompt()` hors du DOM typé : Chromium seulement.
        await (invite as Event & { prompt: () => Promise<void> }).prompt();
        // Consommé : `prompt()` ne peut être appelé qu'une fois par événement.
        setInvite(null);
    };

    return (
        <>
            <EnteteSas accroche={t('sas.androidChrome.accroche')} />

            {invite && (
                <div className="px-6 pt-4">
                    <Button
                        libelle={t('sas.androidChrome.installer')}
                        icone={PlusCircleIcon}
                        onPress={() => void installer()}
                        pleineLargeur
                    />
                </div>
            )}

            <div className="mt-7 px-6">
                <Text variante="petit" ton="attenue" className="font-semibold uppercase tracking-wide">
                    {invite ? t('sas.androidChrome.siRien') : t('sas.androidChrome.pourInstaller')}
                </Text>
            </div>

            <Etapes
                items={[
                    <TexteRiche key="menu" texte={t('sas.androidChrome.etape1')} />,
                    <TexteRiche key="ajouter" texte={t('sas.androidChrome.etape2')} />,
                ]}
            />

            <PiedSas onChoisirConsigne={onChoisirConsigne} />
        </>
    );
}

// ── 5. Android · autre navigateur ───────────────────────────────────────────

export function ConsigneAndroidAutre({ onChoisirConsigne }: ConsigneProps) {
    return (
        <>
            <EnteteSas accroche={t('sas.androidAutre.accroche')} />

            <Etapes
                items={[
                    <TexteRiche key="menu" texte={t('sas.androidAutre.etape1')} />,
                    <TexteRiche key="ajouter" texte={t('sas.androidAutre.etape2')} />,
                    <TexteRiche key="ouvrir" texte={t('sas.androidAutre.etape3')} />,
                ]}
            />

            <PiedSas onChoisirConsigne={onChoisirConsigne} />
        </>
    );
}

// ── 6. Ordinateur — on n'est pas au pouce ───────────────────────────────────

/**
 * Pas de pied de page ici : l'échappatoire sert à corriger un mauvais reniflage
 * sur téléphone, et quelqu'un sur ordinateur n'a pas d'app à installer. Lui
 * proposer les six consignes serait lui proposer six impasses.
 *
 * Le QR code du prototype appartient à #29 (comment on apprend l'existence de
 * l'app) et ne vient pas ici.
 */
export function ConsigneOrdinateur() {
    return (
        <div className="mx-auto flex min-h-full max-w-xl flex-col items-center justify-center px-8 py-20 text-center">
            <img src="/pwa/icon-192.png" alt="" aria-hidden="true" className="h-16 w-16 rounded-2xl" />

            <Text variante="titre" as="h1" className="mt-6">
                {t('sas.ordinateur.titre')}
            </Text>

            <Text variante="petit" ton="attenue" className="mt-3 block max-w-md leading-relaxed">
                <TexteRiche texte={t('sas.ordinateur.texte', { hote: HOTE_WEB })} />
            </Text>

            <a
                href={`https://${HOTE_WEB}`}
                className="mt-7 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-main-color px-5 py-3 text-sm font-semibold text-white"
            >
                <ComputerDesktopIcon aria-hidden="true" className="h-5 w-5" />
                {t('sas.ordinateur.allerSur', { hote: HOTE_WEB })}
            </a>
        </div>
    );
}
